/**
 * NotificationBell — top-bar bell icon with unread badge + dropdown inbox.
 *
 * - Red badge shows unread count.
 * - Opening the panel marks all as "seen" (clears the badge dot) but keeps
 *   unread state until each item is clicked/opened.
 * - Clicking an item marks it read and deep-links to the related entity.
 *
 * Compact Facebook-inspired design: category icons, 2-line clamp, inline
 * timestamps, All/Unread tabs, and 3-dot overflow per item.
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Bell,
  BellOff,
  CheckCheck,
  ChevronDown,
  Ellipsis,
  Key,
  LayoutDashboard,
  Loader2,
  MessageSquare,
  Settings,
  Shield,
  Sparkles,
  Trash2,
  UserPlus,
  Workflow,
  BookOpen,
  ClipboardCheck,
  Database,
} from 'lucide-react'
import { useNavigate } from 'react-router'

import { useNotifications } from './useNotifications'
import type { NotificationItem } from '../../services/api/notifications'
import './NotificationBell.css'

/* ─── helpers ───────────────────────────────────────────────────────── */

function timeAgo(iso?: string | null): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const seconds = Math.floor((Date.now() - then) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d`
  const weeks = Math.floor(days / 7)
  if (weeks < 4) return `${weeks}w`
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/** Map notification category → icon + accent colour. */
function categoryMeta(category: string): { icon: typeof Bell; color: string } {
  switch (category) {
    case 'workflow_run':
      return { icon: Workflow, color: '#8b5cf6' }
    case 'cwb_complete':
    case 'aba_complete':
      return { icon: Sparkles, color: '#f59e0b' }
    case 'agent_response':
      return { icon: MessageSquare, color: '#3b82f6' }
    case 'access_decision':
      return { icon: Shield, color: '#10b981' }
    case 'access_request':
      return { icon: Key, color: '#f97316' }
    case 'user_registration':
      return { icon: UserPlus, color: '#06b6d4' }
    case 'benchmark_assignment':
      return { icon: ClipboardCheck, color: '#ec4899' }
    case 'knowledge_config':
      return { icon: Database, color: '#6366f1' }
    case 'settings':
      return { icon: Settings, color: '#64748b' }
    case 'dashboard':
      return { icon: LayoutDashboard, color: '#0ea5e9' }
    case 'documentation':
      return { icon: BookOpen, color: '#14b8a6' }
    default:
      return { icon: Bell, color: '#6366f1' }
  }
}

type FilterTab = 'all' | 'unread'

/* ─── component ─────────────────────────────────────────────────────── */

const NotificationBell = memo(() => {
  const navigate = useNavigate()
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [activeTab, setActiveTab] = useState<FilterTab>('all')
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)

  const {
    items, unreadItems, unread, unseen,
    hasMore, hasMoreUnread,
    loadingMore, loadingMoreUnread,
    inboxOpen, setInboxOpen,
    markRead, markAllRead, markSeen, dismiss,
    loadMore, loadMoreUnread, fetchUnreadInitial
  } = useNotifications()

  const filteredItems = activeTab === 'unread' ? unreadItems : items
  
  const prevItemsLengthRef = useRef(filteredItems.length)
  const prevTabRef = useRef(activeTab)
  
  const unreadFetchedRef = useRef(false)
  useEffect(() => {
    if (inboxOpen && activeTab === 'unread' && !unreadFetchedRef.current) {
      unreadFetchedRef.current = true
      void fetchUnreadInitial()
    }
  }, [inboxOpen, activeTab, fetchUnreadInitial])

  // Track when more items are loaded
  useEffect(() => {
    if (activeTab !== prevTabRef.current) {
      prevTabRef.current = activeTab
      prevItemsLengthRef.current = filteredItems.length
      if (listRef.current && typeof listRef.current.scrollTo === 'function') {
        listRef.current.scrollTo({ top: 0, behavior: 'auto' })
      }
      return
    }

    if (filteredItems.length > prevItemsLengthRef.current) {
      if (prevItemsLengthRef.current > 0) {
        const startIndex = prevItemsLengthRef.current

        // Scroll down to the first newly loaded item
        if (listRef.current && typeof listRef.current.scrollTo === 'function') {
          const children = listRef.current.children
          if (children && children.length > startIndex) {
            const firstNewItem = children[startIndex] as HTMLElement
            if (firstNewItem) {
              listRef.current.scrollTo({
                top: firstNewItem.offsetTop,
                behavior: 'smooth'
              })
            }
          }
        }
      }
    }
    prevItemsLengthRef.current = filteredItems.length
  }, [filteredItems.length, activeTab])

  // Close on outside click
  useEffect(() => {
    if (!inboxOpen) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setInboxOpen(false)
        setMenuOpenId(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [inboxOpen, setInboxOpen])

  // Close overflow menu on any scroll
  useEffect(() => {
    if (!menuOpenId || !listRef.current) return
    const el = listRef.current
    const handler = () => setMenuOpenId(null)
    el.addEventListener('scroll', handler, { passive: true })
    return () => el.removeEventListener('scroll', handler)
  }, [menuOpenId])

  const togglePanel = useCallback(() => {
    setInboxOpen((prev) => !prev)
    setMenuOpenId(null)
  }, [setInboxOpen])

  useEffect(() => {
    if (inboxOpen && unseen > 0) {
      void markSeen()
    }
  }, [inboxOpen, unseen, markSeen])

  const handleItemClick = useCallback(
    (item: NotificationItem) => {
      if (!item.read) void markRead(item.id)
      setInboxOpen(false)
      setMenuOpenId(null)
      if (item.deep_link) {
        navigate(item.deep_link)
      }
    },
    [markRead, navigate, setInboxOpen],
  )

  const badge = unread > 99 ? '99+' : String(unread)

  return (
    <div className="notif-bell" ref={containerRef}>
      <button
        className={`notif-bell__button${unseen > 0 ? ' notif-bell__button--active' : ''}`}
        onClick={togglePanel}
        title={unread > 0 ? `${unread} unread notification${unread !== 1 ? 's' : ''}` : 'Notifications'}
        aria-label="Notifications"
        aria-haspopup="true"
        aria-expanded={inboxOpen}
      >
        <Bell size={18} />
        {unread > 0 && <span className="notif-bell__badge">{badge}</span>}
      </button>

      {inboxOpen && (
        <div className="notif-panel" role="dialog" aria-label="Notification inbox">
          {/* ── Header ── */}
          <div className="notif-panel__header">
            <span className="notif-panel__title">Notifications</span>
            <div className="notif-panel__header-actions">
              <div className="notif-panel__tabs">
                <button
                  className={`notif-panel__tab${activeTab === 'all' ? ' notif-panel__tab--active' : ''}`}
                  onClick={() => setActiveTab('all')}
                >
                  All
                </button>
                <button
                  className={`notif-panel__tab${activeTab === 'unread' ? ' notif-panel__tab--active' : ''}`}
                  onClick={() => setActiveTab('unread')}
                >
                  Unread{unread > 0 && <span className="notif-panel__tab-count">{unread}</span>}
                </button>
              </div>
              {unread > 0 && (
                <button
                  className="notif-panel__mark-all"
                  onClick={() => void markAllRead()}
                  title="Mark all as read"
                >
                  <CheckCheck size={15} />
                </button>
              )}
            </div>
          </div>

          {/* ── List ── */}
          <div className="notif-panel__list" ref={listRef}>
            {filteredItems.length === 0 ? (
              <div className="notif-panel__empty">
                <BellOff size={32} strokeWidth={1.5} />
                <span>
                  {activeTab === 'unread' ? 'No unread notifications' : "You\u2019re all caught up."}
                </span>
              </div>
            ) : (
              filteredItems.map((item, index) => {
                const isNearBottom = index >= filteredItems.length - 2;
                const meta = categoryMeta(item.category)
                const Icon = meta.icon
                return (
                  <div
                    key={item.id}
                    className={`notif-item${item.read ? '' : ' notif-item--unread'}`}
                    onClick={() => handleItemClick(item)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleItemClick(item)
                    }}
                  >
                    {/* Category icon avatar */}
                    <div className="notif-item__avatar" style={{ '--notif-accent': meta.color } as React.CSSProperties}>
                      <Icon size={16} />
                    </div>

                    {/* Text content */}
                    <div className="notif-item__content">
                      <p className="notif-item__text">
                        <span className="notif-item__title">{item.title}</span>
                        {item.body && (
                          <>
                            {' '}
                            <span className="notif-item__body">{item.body}</span>
                          </>
                        )}
                      </p>
                    </div>

                    {/* Right side: overflow menu + unread dot + time */}
                    <div className="notif-item__actions">
                      <button
                        className="notif-item__menu-trigger"
                        onClick={(e) => {
                          e.stopPropagation()
                          setMenuOpenId((prev) => (prev === item.id ? null : item.id))
                        }}
                        title="More options"
                        aria-label="More options"
                      >
                        <Ellipsis size={16} />
                      </button>
                      {!item.read && <span className="notif-item__dot" aria-hidden="true" />}
                      <span className="notif-item__time">{timeAgo(item.created_at)}</span>

                      {menuOpenId === item.id && (
                        <div className={`notif-item__menu${isNearBottom ? ' notif-item__menu--up' : ''}`}>
                          {!item.read && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                void markRead(item.id)
                                setMenuOpenId(null)
                              }}
                            >
                              <CheckCheck size={14} />
                              Mark as read
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              void dismiss(item.id)
                              setMenuOpenId(null)
                            }}
                          >
                            <Trash2 size={14} />
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>

          {/* ── Footer ── */}
          {((hasMore && activeTab === 'all') || (hasMoreUnread && activeTab === 'unread')) && (
            <div className="notif-panel__footer">
              <button
                type="button"
                className={`notif-panel__load-more${(activeTab === 'all' ? loadingMore : loadingMoreUnread) ? ' notif-panel__load-more--loading' : ''}`}
                disabled={activeTab === 'all' ? loadingMore : loadingMoreUnread}
                onClick={() => void (activeTab === 'all' ? loadMore() : loadMoreUnread())}
              >
                {(activeTab === 'all' ? loadingMore : loadingMoreUnread) ? (
                  <>
                    <Loader2 size={14} className="notif-load-spinner" />
                    <span>Loading…</span>
                  </>
                ) : (
                  <span>See previous notifications</span>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
})

NotificationBell.displayName = 'NotificationBell'

export default NotificationBell
